import { RouterSpinner } from "@/components/common/RouterSpinner";
import { AuthErrorPage } from "@/components/pages/AuthErrorPage";
import { Toaster } from "@/components/ui/sonner";
import type { NavT } from "@/utils/nav";
import { Analytics } from "@vercel/analytics/react";

import type { QueryClient } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import {
	createRootRouteWithContext,
	Outlet,
	ScrollRestoration,
	useNavigate,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/router-devtools";
import { Suspense, useEffect, useState } from "react";
import { isMobile } from "react-device-detect";
import ErrorPage from "../components/pages/ErrorPage";
import NotFoundPage from "../components/pages/NotFoundPage";
import useAuthQuery from "../features/auth/api/useAuthQuery";
import { DemoBanner } from "../features/demo/DemoBanner";
import { cn } from "../lib/utils";
import type { Auth } from "../utils/auth";

export const Route = createRootRouteWithContext<{
	queryClient: QueryClient;
	auth: Auth;
	dark: boolean;
	nav: NavT;
}>()({
	component: RootComponent,
	errorComponent: ({ error, reset }) => (
		<ErrorPage error={error} reset={reset} />
	),
	notFoundComponent: NotFoundPage,
});

function RootComponent() {
	const navigate = useNavigate();
	const [authError, setAuthError] = useState<
		{
			title: string;
			description: string;
		} | null
	>(null);
	// Using the auth query allows early sign-in. For example if accepting and invite as a new user
	useAuthQuery();

	const resetAuthError = () => {
		setAuthError(null);
	};

	const handleSupportClick = () => {
		resetAuthError();
		navigate({ to: "/support" });
	};

	const handleBackToLogin = () => {
		resetAuthError();
		navigate({ to: "/login" });
	};

	useEffect(() => {
		const handleHashChange = () => {
			const hash = window.location.hash;

			if (hash) {
				const params = new URLSearchParams(hash.substring(1));
				const error = params.get("error");
				const errorCode = params.get("error_code");
				const errorDescription = params.get("error_description");

				console.log("Parsed params:", {
					error,
					errorCode,
					errorDescription,
				});

				if (error === "access_denied" && errorCode === "otp_expired") {
					navigate({ to: "/auth-error" });
				}
			}
		};

		// Run on mount
		handleHashChange();

		// Listen for hash changes
		window.addEventListener("hashchange", handleHashChange);

		// Cleanup
		return () => {
			window.removeEventListener("hashchange", handleHashChange);
		};
	}, []);

	if (authError) {
		return <AuthErrorPage resetAuthError={resetAuthError} />;
	}

	return (
		<>
			<ScrollRestoration />

			{import.meta.env.PROD && <Analytics />}
			<Toaster
				// richColors
				duration={isMobile ? 2500 : 4000}
				expand={true}
				position={isMobile ? "top-center" : "bottom-left"}
				toastOptions={{
					unstyled: true,
					classNames: {
						toast:
							"print:hidden flex flex-row gap-x-2 p-4 rounded-lg items-center justify-center bg-white dark:bg-slate-800 dark:text-white text-md border-2",
						title: " text-slate-800 dark:text-slate-100",
						description: "text-slate-600 dark:text-slate-300",
						success: "border-indigo-600 text-indigo-600",
						warning: "border-amber-600 text-amber-600",
						error: "border-red-600 text-red-600",
						info: "border-indigo-600 text-indigo-600",
						actionButton:
							"rounded-sm flex bg-indigo-600 text-white p-2 text-xs w-fit text-nowrap items-center justify-center",
						cancelButton: "text-red-500 border-red-500 border-2",
						closeButton: "text-red-500 border-red-500 border-2",
					},
				}}
			/>
			<RouterSpinner />
			<DemoBanner />
			<Outlet />
			<div
				className={cn(
					window.location.hostname.includes("localhost")
						? "inline"
						: "hidden",
				)}
			>
				<ReactQueryDevtools
					initialIsOpen={false}
					buttonPosition="bottom-left"
				/>
				<Suspense>
					<TanStackRouterDevtools position="bottom-right" />
				</Suspense>
			</div>
		</>
	);
}
